import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CommentsSection from '../components/CommentsSection';
import { categories } from '../mock';
import { useFavorites } from '../context/FavoritesContext';
import { MapPin, Phone, Mail, User, Calendar, ArrowLeft, Map, Heart, Flag } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/axios';

const OfferDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    const loadOffer = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/offers/${id}`);
        setOffer(response.data);
      } catch (error) {
        console.error('Erreur chargement offre:', error);
        toast.error('Erreur', { description: 'Impossible de charger l\'offre' });
      } finally {
        setLoading(false);
      }
    };

    loadOffer();
  }, [id]);

  const handleSubmitReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Erreur', { description: 'Veuillez indiquer une raison' });
      return;
    }

    try {
      setSubmittingReport(true);
      await api.post('/reports', {
        offer_id: offer.id,
        reason: reportReason,
        description: reportDescription
      });
      
      toast.success('Signalement envoyé', { 
        description: 'Merci, notre équipe examinera votre signalement' 
      });
      
      setReportDialogOpen(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Erreur signalement:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi du signalement';
      toast.error('Erreur', { description: errorMsg });
    } finally {
      setSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
        <Navbar />
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'offre...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
        <Navbar />
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-slate-800">Offre non trouvée</h1>
          <Button onClick={() => navigate('/carte')} className="mt-4">
            Retour à la carte
          </Button>
        </div>
      </div>
    );
  }

  const categoryInfo = offer.category === 'allianceHumaine' 
    ? categories.allianceHumaine 
    : categories.allianceEvasion;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/carte')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la carte
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Image */}
              <Card className="overflow-hidden border-none shadow-xl">
                <div className="relative h-96">
                  <img
                    src={offer.image_url || 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600'}
                    alt={offer.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <button
                      onClick={() => toggleFavorite(offer.id)}
                      className="bg-white/90 hover:bg-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
                    >
                      <Heart
                        className={`w-6 h-6 ${
                          isFavorite(offer.id)
                            ? 'fill-red-500 text-red-500'
                            : 'text-gray-600 hover:text-red-500'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge
                      className="text-white text-base px-4 py-2"
                      style={{ backgroundColor: categoryInfo.color }}
                    >
                      {offer.subcategory}
                    </Badge>
                  </div>
                </div>
              </Card>

              {/* Details */}
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-3xl mb-2">{offer.title}</CardTitle>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>Ajouté le {new Date(offer.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">Description</h3>
                    <p className="text-gray-700 leading-relaxed">{offer.description}</p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      Localisation
                    </h3>
                    <p className="text-gray-700 mb-2">{offer.address}</p>
                    {offer.postal_code && (
                      <Badge variant="outline" className="mb-3 bg-purple-50 text-purple-700 border-purple-300">
                        📍 {offer.postal_code}
                      </Badge>
                    )}
                    {!offer.postal_code && offer.department && (
                      <Badge variant="outline" className="mb-3 bg-purple-50 text-purple-700 border-purple-300">
                        📍 Dept. {offer.department}
                      </Badge>
                    )}
                    <Link to="/carte">
                      <Button variant="outline" className="mt-3">
                        <Map className="w-4 h-4 mr-2" />
                        Voir sur la carte
                      </Button>
                    </Link>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">Catégorie</h3>
                    <div className="flex gap-2">
                      <Badge
                        className="text-white"
                        style={{ backgroundColor: categoryInfo.color }}
                      >
                        {categoryInfo.name}
                      </Badge>
                      <Badge variant="outline">{offer.subcategory}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Card */}
            <div className="space-y-6">
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle>Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${offer.contact.name}`} />
                      <AvatarFallback>
                        <User className="w-8 h-8" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-lg">{offer.contact.name}</p>
                      <p className="text-sm text-gray-600">Organisateur</p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <a
                      href={`tel:${offer.contact.phone}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Phone className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Téléphone</p>
                        <p className="font-medium">{offer.contact.phone}</p>
                      </div>
                    </a>

                    <a
                      href={`mailto:${offer.contact.email}`}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-sm">{offer.contact.email}</p>
                      </div>
                    </a>
                  </div>

                  <a
                    href={`mailto:${offer.contact.email}?subject=Contact depuis Alliance Solidarité - ${offer.title}`}
                    className="w-full"
                  >
                    <Button className="w-full bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 mt-4">
                      <Mail className="w-4 h-4 mr-2" />
                      Contacter
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-lg mb-2">Vous proposez une offre ?</h3>
                  <p className="text-sm text-emerald-50 mb-4">
                    Partagez votre initiative solidaire avec la communauté
                  </p>
                  <Link to="/ajouter">
                    <Button variant="secondary" className="w-full">
                      Ajouter une offre
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Report Button */}
              <Button
                variant="destructive"
                className="w-full bg-red-500 hover:bg-red-600"
                onClick={() => setReportDialogOpen(true)}
              >
                <Flag className="w-4 h-4 mr-2" />
                Signaler cette offre
              </Button>
            </div>
          </div>

          {/* Section Commentaires */}
          <div className="mt-8">
            <CommentsSection offerId={offer.id} />
          </div>
        </div>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signaler cette offre</DialogTitle>
            <DialogDescription>
              Indiquez-nous pourquoi vous signalez cette offre. Notre équipe de modération examinera votre signalement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Raison du signalement *</Label>
              <Textarea
                id="reason"
                placeholder="Ex: Contenu inapproprié, fausse information, spam..."
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Détails supplémentaires (optionnel)</Label>
              <Textarea
                id="description"
                placeholder="Ajoutez plus de détails si nécessaire..."
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportDialogOpen(false)}
              disabled={submittingReport}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmitReport}
              disabled={submittingReport}
            >
              {submittingReport ? 'Envoi...' : 'Envoyer le signalement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default OfferDetails;